import React, { useEffect, useState } from "react";
import { Eye, EyeOff, Save, House } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { bustAssets } from "@/lib/assets";
import { ImageField } from "@/admin/MediaPicker";

const PRINCIPLES = {
  1: "Sostenibilità",
  2: "Sincretismo Culturale",
  3: "Riconnessione con la natura",
  4: "Esperienza trasformativa / Spirituale",
};

export default function AdminHome() {
  const [settings, setSettings] = useState(null);
  const [langTab, setLangTab] = useState("it");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const setItem = (key, patch) => setSettings((s) => ({ ...s, manifesto: s.manifesto.map((m) => (m.key === key ? { ...m, ...patch } : m)) }));
  const setF = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/admin/settings", settings);
      setSettings(r.data);
      bustAssets();
      toast.success("Home aggiornata");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <p className="text-sand text-sm">Carico…</p>;

  return (
    <div data-testid="admin-home">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl flex items-center gap-3"><House className="h-6 w-6 text-copper" /> Home & Sezioni</h1>
          <p className="text-cream2 text-sm mt-1">Immagine di apertura della Home, visibilità dei quattro principi e sfondi delle sezioni "Scopriamo assieme…". Le immagini si scelgono dalla libreria Media CMS.</p>
        </div>
        <button onClick={save} disabled={busy} className="btn-copper !px-5 !py-2.5 disabled:opacity-50" data-testid="home-save"><Save className="h-4 w-4" /> {busy ? "Salvo…" : "Salva"}</button>
      </div>

      <h2 className="font-serif text-2xl mt-10 mb-1">Immagine principale della Home</h2>
      <p className="text-sand text-sm mb-4">La foto grande di apertura della Home. Senza immagine resta quella predefinita.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ImageField label="Immagine di apertura" value={settings.hero_image} onChange={(v) => setF("hero_image", v)} testId="home-hero-image" />
      </div>

      <h2 className="font-serif text-2xl mt-12 mb-1">Il nostro manifesto — quattro principi</h2>
      <p className="text-sand text-sm mb-4">Scrivi qui titolo e testo di ogni principio: se lasci i campi vuoti resta il testo predefinito del sito. Le linguette EN/ES servono per le traduzioni (vuote = si usa l'italiano).</p>
      <div className="flex items-center gap-2 mb-4">
        {["it", "en", "es"].map((lg) => (
          <button
            key={lg}
            type="button"
            onClick={() => setLangTab(lg)}
            className={"font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors " + (langTab === lg ? "border-copper text-copperlight" : "border-white/10 text-sand hover:text-cream")}
            data-testid={`home-manifesto-lang-tab-${lg}`}
          >
            {lg}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="home-manifesto-list">
        {settings.manifesto.map((m) => {
          const tr = (m.translations || {})[langTab] || {};
          const val = (k) => (langTab === "it" ? m[k] || "" : tr[k] || "");
          const setVal = (k, v) =>
            langTab === "it"
              ? setItem(m.key, { [k]: v })
              : setItem(m.key, { translations: { ...(m.translations || {}), [langTab]: { ...tr, [k]: v } } });
          return (
            <div key={m.key} className={"card-dark p-5 transition-opacity " + (m.visible ? "" : "opacity-60")} data-testid={`home-manifesto-${m.key}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="font-serif text-xl text-cream"><span className="text-copper font-mono text-sm mr-2">0{m.key}</span>{m.title || PRINCIPLES[m.key]}</p>
                <button
                  type="button"
                  onClick={() => setItem(m.key, { visible: !m.visible })}
                  className={"font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border flex items-center gap-2 " + (m.visible ? "border-copper text-copperlight" : "border-white/10 text-sand")}
                  data-testid={`home-manifesto-visible-${m.key}`}
                >
                  {m.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {m.visible ? "Visibile in Home" : "Nascosto"}
                </button>
              </div>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="label">Titolo {langTab !== "it" && `(${langTab.toUpperCase()})`}</label>
                  <input
                    className="field"
                    placeholder={langTab === "it" ? PRINCIPLES[m.key] : "Lascia vuoto per usare l'italiano"}
                    value={val("title")}
                    onChange={(e) => setVal("title", e.target.value)}
                    data-testid={`home-manifesto-title-${m.key}`}
                  />
                </div>
                <div>
                  <label className="label">Testo {langTab !== "it" && `(${langTab.toUpperCase()})`}</label>
                  <textarea
                    rows="4"
                    className="field resize-none"
                    placeholder={langTab === "it" ? "Lascia vuoto per usare il testo predefinito del sito" : "Lascia vuoto per usare l'italiano"}
                    value={val("text")}
                    onChange={(e) => setVal("text", e.target.value)}
                    data-testid={`home-manifesto-text-${m.key}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <h2 className="font-serif text-2xl mt-12 mb-1">Pulsante "Guarda il film"</h2>
      <p className="text-sand text-sm mb-4">Incolla il link esterno del video (YouTube o Vimeo): verrà mostrato nella finestra che si apre dal pulsante in Home. Se lasci vuoto, resta il video destinato dalla libreria Media CMS.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="label">Link del film</span>
          <input
            type="url"
            className="field"
            placeholder="https://www.youtube.com/watch?v=..."
            value={settings.home_film_url || ""}
            onChange={(e) => setF("home_film_url", e.target.value)}
            data-testid="home-film-url"
          />
        </label>
      </div>

      <h2 className="font-serif text-2xl mt-12 mb-1">Sfondi "Scopriamo assieme se questa esperienza è adatta a te"</h2>
      <p className="text-sand text-sm mb-4">Sezione finale con il pulsante che porta al modulo. Senza immagine resta lo sfondo scuro attuale.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ImageField label="Home" value={settings.cta_bg_home} onChange={(v) => setF("cta_bg_home", v)} testId="home-cta-bg-home" />
        <ImageField label="Pagine spedizione" value={settings.cta_bg_tour} onChange={(v) => setF("cta_bg_tour", v)} testId="home-cta-bg-tour" />
        <ImageField label="Pagine tour leader" value={settings.cta_bg_leader} onChange={(v) => setF("cta_bg_leader", v)} testId="home-cta-bg-leader" />
      </div>

      <div className="flex justify-end mt-8">
        <button onClick={save} disabled={busy} className="btn-copper !px-6 !py-2.5 disabled:opacity-50" data-testid="home-save-bottom"><Save className="h-4 w-4" /> {busy ? "Salvo…" : "Salva modifiche"}</button>
      </div>
    </div>
  );
}
